import { isTestEnv } from './canvasx';
import { fakeMeasureText } from './testUtils';

const fontDiv = document.createElement('div');
const fontMapping = new Map<
  string,
  { fontSize: number; fontFamily: string; fontWeight: string }
>();

export const DEFAULT_FONT_SIZE = 10;

export function parseFont(font: CanvasRenderingContext2D['font']): {
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
} {
  if (!fontMapping.has(font)) {
    fontDiv.style.font = font;

    fontMapping.set(font, {
      fontSize: parseInt(fontDiv.style.fontSize, 10),
      fontFamily: fontDiv.style.fontFamily,
      fontWeight: fontDiv.style.fontWeight,
    });
  }

  const { fontSize, fontFamily, fontWeight } = fontMapping.get(font)!;

  return { fontSize, fontFamily, fontWeight };
}

export function measureText(
  ctx: CanvasRenderingContext2D,
  text: string
): DimensionStruct {
  // For tests, we need to fake text dimensions
  const { fontSize } = parseFont(ctx.font);
  if (isTestEnv) {
    return fakeMeasureText(text, fontSize);
  }
  const textMetrics = ctx.measureText(text);
  const {
    width,
    fontBoundingBoxAscent = 0,
    fontBoundingBoxDescent = 0,
  } = textMetrics;

  // When exporting, measuring text doesn't always have the correct ascent/descent value
  // This seems to be a bug in svgcanvas
  const boundingBoxHeight = Math.max(
    fontBoundingBoxAscent,
    fontBoundingBoxDescent
  );
  return {
    width,
    height: boundingBoxHeight || fontSize,
  };
}

/**
 * Renders text clipped/truncated to a specified width.
 */
export function truncateText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  size: number,
  overflow?: string
) {
  if (maxWidth <= 0 || text.length === 0) return;

  const overflowWidth = overflow ? measureText(ctx, overflow).width : 0;

  if (isTestEnv) {
    // Currently the mock canvas doesn't support clipping
    const width = measureText(ctx, text).width;

    if (width <= maxWidth) {
      renderText(ctx, text);
    }

    // Find the shortest bit of text that can be rendered
    let truncatedWidth = overflowWidth;
    let length = 0;
    for (let i = 0; i < text.length; i++) {
      const { width } = measureText(ctx, text[i]);

      if (truncatedWidth + width > maxWidth) {
        break;
      }

      truncatedWidth += width;
      length++;
    }

    renderText(ctx, text.slice(0, length));
  } else {
    // Render the text inside a clipped rectangle. Wwe add a gap on the
    // left/top/bottom to ensure we don't clip those edges.
    ctx.save();
    ctx.beginPath();
    ctx.rect(-size, -size, maxWidth + size - overflowWidth, 2 * size);
    ctx.clip();
    renderText(ctx, text);
    ctx.restore();
  }

  if (overflow) {
    ctx.save();
    ctx.translate(maxWidth - overflowWidth, 0);
    renderText(ctx, overflow);
    ctx.restore();
  }
}

/**
 * Renders text.
 */
export function renderText(ctx: CanvasRenderingContext2D, text: string) {
  ctx.fillText(text, 0, 0);
}

export function fontForProps({
  face = 'Arial',
  size = DEFAULT_FONT_SIZE,
  bold = false,
  italic = false,
  weight,
}: Partial<FontStruct>) {
  if (!Array.isArray(face)) {
    face = [face];
  }

  face = face.map(face =>
    face.includes(' ') ? JSON.stringify(face).replace(/"/g, "'") : face
  );

  return [
    bold && 'bold',
    italic && 'italic',
    weight && `${weight}`,
    `${size}px ${face}`,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Applies font styles as defined by the font struct. Appllies the minimal amount of changes
 * as possible, so that font styles can be inherited.
 */
export function applyFontStyles(
  ctx: CanvasRenderingContext2D,
  font: Partial<FontStruct>
) {
  const {
    color,
    face,
    size,
    bold,
    italic,
    weight,
    textAlign,
    textBaseline = 'top',
  } = font;

  // Providing any of these values will overwrite all existing font styles
  if (face || size || bold || italic || weight) {
    ctx.font = fontForProps(font);
  }

  if (color) {
    ctx.fillStyle = color;
  }

  if (textAlign) {
    ctx.textAlign = textAlign;
  }

  ctx.textBaseline = textBaseline;
}
